/**
 * Punkt R (Mockup „Paket-Aufteilung", freigegeben 13.10.2026): „davon Speisen · 7 %" und
 * „davon Getränke · 19 %" am Paket. Gespeichert wird NUR der Getränkeanteil (`drinksShareCents`,
 * dieselbe Einheit wie der Paketpreis); das andere Feld ist immer der Rest. Leer = MAESTRO schätzt
 * wie bisher (70/30). Ein Anteil über dem Paketpreis wird nicht gespeichert.
 */
import { useEffect, useState } from "react";
import { Icon, Input } from "./components";
import { centsToEuros, money, parseGermanEurosToCents } from "./format";

export function PaketAufteilungFelder({
  preisCents,
  gespeichertCents,
  preisGeaendert,
  onSpeichern,
}: {
  /** Aktueller (auch gerade getippter) Paketpreis — pro Gast bzw. pauschal. */
  preisCents: number;
  gespeichertCents: number | null;
  /** Der Paketpreis wurde in dieser Sitzung geändert — dann steht der graue Hinweis. */
  preisGeaendert: boolean;
  onSpeichern: (drinksShareCents: number | null) => void;
}) {
  const [getraenke, setGetraenke] = useState("");
  const [speisen, setSpeisen] = useState("");
  // null = leer (Schätzung), NaN = nicht lesbar.
  const [anteil, setAnteil] = useState<number | null>(gespeichertCents);

  useEffect(() => {
    setAnteil(gespeichertCents);
    setGetraenke(gespeichertCents == null ? "" : centsToEuros(gespeichertCents));
  }, [gespeichertCents]);
  useEffect(() => {
    setSpeisen(anteil == null || Number.isNaN(anteil) ? "" : centsToEuros(preisCents - anteil));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preisCents, gespeichertCents]);

  const lies = (text: string): number | null => (text.trim() === "" ? null : (parseGermanEurosToCents(text) ?? Number.NaN));
  const fehler = anteil != null && (Number.isNaN(anteil) || anteil < 0 || anteil > preisCents);

  function getraenkeTippen(text: string) {
    setGetraenke(text);
    const c = lies(text);
    setAnteil(c);
    setSpeisen(c == null || Number.isNaN(c) ? "" : centsToEuros(preisCents - c));
  }
  function speisenTippen(text: string) {
    setSpeisen(text);
    const c = lies(text);
    const rest = c == null ? null : Number.isNaN(c) ? Number.NaN : preisCents - c;
    setAnteil(rest);
    setGetraenke(rest == null || Number.isNaN(rest) ? "" : centsToEuros(rest));
  }
  function speichern() {
    if (fehler || anteil === gespeichertCents) return;
    onSpeichern(anteil);
  }

  const rand = fehler ? "!border-error" : "";
  return (
    <div className="sm:col-span-2" data-testid="paket-aufteilung">
      <span className="mb-1 block font-body-sm text-body-sm font-semibold">Aufteilung für die Steuer</span>
      <div className="grid grid-cols-2 gap-3">
        <label className="block min-w-0">
          <span className="mb-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">davon Speisen · 7 %</span>
          <Input value={speisen} inputMode="decimal" aria-invalid={fehler || undefined} className={rand} onChange={(e) => speisenTippen(e.target.value)} onBlur={speichern} />
        </label>
        <label className="block min-w-0">
          <span className="mb-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">davon Getränke · 19 %</span>
          <Input value={getraenke} inputMode="decimal" aria-invalid={fehler || undefined} className={rand} onChange={(e) => getraenkeTippen(e.target.value)} onBlur={speichern} />
        </label>
      </div>
      <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Tippst du in ein Feld, füllt sich das andere mit dem Rest. Leer lassen: MAESTRO schätzt die Aufteilung wie bisher.
      </span>
      {fehler ? (
        <p role="alert" className="mt-2 flex items-start gap-1.5 font-body-sm text-[12.5px] text-error">
          <Icon name="error" className="mt-px shrink-0 text-[15px]" /> Ein Anteil ist höher als der Paketpreis ({money(preisCents)}). Nicht gespeichert.
        </p>
      ) : (
        preisGeaendert &&
        gespeichertCents != null && (
          <p className="mt-2 flex items-start gap-1.5 font-body-sm text-[12.5px] text-[#6F675A] dark:text-[#b8ae9e]">
            <Icon name="info" className="mt-px shrink-0 text-[15px]" /> Paketpreis geändert. Getränke bleiben bei {money(gespeichertCents)}, Speisen jetzt {money(preisCents - gespeichertCents)}.
          </p>
        )
      )}
    </div>
  );
}
